import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type {
  ThematicEntity,
  NewThematicEntity,
  UpdateThematicEntity,
} from '@/types/thematicMaps';

export const useThematicEntities = (mapId: string | undefined) => {
  const [entities, setEntities] = useState<ThematicEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchEntities = useCallback(async () => {
    if (!user || !mapId) return;

    try {
      setLoading(true);
      // PostgREST caps at 1000 rows per request — fetch in batches via .range()
      const batchSize = 1000;
      let allData: ThematicEntity[] = [];
      let from = 0;

      while (true) {
        const { data, error } = await (supabase as any)
          .from('thematic_entities')
          .select('*')
          .eq('map_id', mapId)
          .order('canonical_name', { ascending: true })
          .range(from, from + batchSize - 1);

        if (error) throw error;
        const batch = (data || []) as ThematicEntity[];
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        from += batchSize;
      }

      setEntities(allData);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania encji';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createEntity = useCallback(async (
    entityData: Omit<NewThematicEntity, 'user_id' | 'map_id'>
  ): Promise<ThematicEntity | null> => {
    if (!user || !mapId) return null;

    try {
      const { data, error } = await (supabase as any)
        .from('thematic_entities')
        .insert({
          user_id: user.id,
          map_id: mapId,
          ...entityData,
        })
        .select()
        .single();

      if (error) throw error;

      setEntities(prev => [...prev, data as ThematicEntity]);
      toast({ title: 'Sukces', description: 'Encja dodana' });
      return data as ThematicEntity;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd tworzenia encji';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return null;
    }
  }, [user, mapId, toast]);

  const createEntitiesBulk = useCallback(async (
    entitiesData: Omit<NewThematicEntity, 'user_id' | 'map_id'>[]
  ): Promise<ThematicEntity[]> => {
    if (!user || !mapId) return [];

    try {
      // Lowercase canonical_name dla spojnosci z uq_entity_per_map (expression lower())
      // + uq_entity_per_map_plain (plain) — oba constraints lapia te same duplikaty.
      const rows = entitiesData.map(e => ({
        user_id: user.id,
        map_id: mapId,
        ...e,
        canonical_name: (e.canonical_name ?? '').toLowerCase().trim(),
      }));

      const { data, error } = await (supabase as any)
        .from('thematic_entities')
        .upsert(rows, {
          onConflict: 'map_id,canonical_name',
          ignoreDuplicates: true,
        })
        .select();

      if (error) throw error;

      const inserted = (data as ThematicEntity[]) || [];
      const skipped = rows.length - inserted.length;
      toast({
        title: 'Sukces',
        description: skipped > 0
          ? `Dodano ${inserted.length} encji (${skipped} duplikatow pominieto)`
          : `Dodano ${inserted.length} encji`,
      });
      await fetchEntities();
      return inserted;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd dodawania encji';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return [];
    }
  }, [user, mapId, toast, fetchEntities]);

  const updateEntity = useCallback(async (id: string, updates: UpdateThematicEntity): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_entities')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setEntities(prev => prev.map(e =>
        e.id === id ? { ...e, ...updates } : e
      ));
      toast({ title: 'Sukces', description: 'Encja zaktualizowana' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd aktualizacji encji';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const deleteEntity = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_entities')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setEntities(prev => prev.filter(e => e.id !== id));
      toast({ title: 'Sukces', description: 'Encja usunięta' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania encji';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const deleteAllEntities = useCallback(async (): Promise<boolean> => {
    if (!mapId) return false;
    try {
      const { error } = await (supabase as any)
        .from('thematic_entities')
        .delete()
        .eq('map_id', mapId);

      if (error) throw error;

      setEntities([]);
      toast({ title: 'Sukces', description: 'Wszystkie encje usunięte' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania encji';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [mapId, toast]);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchEntities();
    }
  }, [user?.id, mapId, fetchEntities]);

  const { isLive } = useRealtimeSubscription({
    channel: `entities_${mapId || 'none'}`,
    table: 'thematic_entities',
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(user?.id && mapId),
    onChange: fetchEntities,
  });

  return {
    entities,
    loading,
    isLive,
    createEntity,
    createEntitiesBulk,
    updateEntity,
    deleteEntity,
    deleteAllEntities,
    refreshEntities: fetchEntities,
  };
};
